import type { QueryClient } from '@tanstack/react-query';
import type { OrgModelProjection } from '../models/OrgModelProjection';
import { invalidateOrgPlanningQueries } from './invalidateOrgPlanningQueries';
import { liveOrgModelQueryKey } from './orgModelQueryKeys';
import { PositionCommandSettlementError, settlePositionCommand } from './positionCommandSettlement';

interface QueuedOrgPlanningMutation<T> {
  readonly queueKey: string;
  readonly targetRef: string;
  mutation: (signal: AbortSignal) => Promise<T>;
  readonly abortController: AbortController;
  queryClient?: QueryClient;
  revision: number;
  requiredRevision?: number;
  allowFailedPrerequisites?: boolean;
  waiters: Array<{
    resolve: (value: unknown) => void;
    reject: (reason: unknown) => void;
  }>;
}

interface CanonicalConvergenceBarrier {
  readonly queryClient: QueryClient;
  readonly reconcile: () => Promise<void>;
  promise: Promise<void>;
}

/** A committed external operation reached a terminal failure and must be
 * surfaced to the command that was waiting behind it, not retried forever. */
export class OrgPlanningCommandBarrierBlockedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'OrgPlanningCommandBarrierBlockedError';
  }
}

interface OrgPlanningCommandLane {
  running: boolean;
  readonly order: string[];
  readonly pending: Map<string, QueuedOrgPlanningMutation<unknown>>;
  readonly coalescibleByTarget: Map<string, string>;
  nextRevision: number;
  readonly failedByTarget: Map<string, number>;
  barrier?: CanonicalConvergenceBarrier;
  active?: QueuedOrgPlanningMutation<unknown>;
}

export interface OrgPlanningTransitionOptions {
  /** Discard may close the last accepted head even when a newer local save failed. */
  readonly allowFailedPrerequisites?: boolean;
  /** Makes lifecycle clicks first-click-wins across every mounted surface. */
  readonly lifecycleKey?: string;
}

const lanes = new Map<string, OrgPlanningCommandLane>();
const lifecycleTransitions = new Map<string, { key: string; promise: Promise<unknown> }>();
let queueSequence = 0;
const ORG_PLANNING_COMMAND_TIMEOUT_MS = 15_000;

export function orgPlanningCommandDeadlineSignal(parent?: AbortSignal): {
  signal: AbortSignal;
  timedOut: () => boolean;
  dispose: () => void;
} {
  const controller = new AbortController();
  let timeoutReached = false;
  const onParentAbort = () => controller.abort(parent?.reason);
  if (parent?.aborted) onParentAbort();
  else parent?.addEventListener('abort', onParentAbort, { once: true });
  const timer = setTimeout(() => {
    timeoutReached = true;
    controller.abort(new DOMException('Organization command timed out.', 'TimeoutError'));
  }, ORG_PLANNING_COMMAND_TIMEOUT_MS);
  return {
    signal: controller.signal,
    timedOut: () => timeoutReached,
    dispose: () => {
      clearTimeout(timer);
      parent?.removeEventListener('abort', onParentAbort);
    },
  };
}

function laneFor(context: string): OrgPlanningCommandLane {
  const existing = lanes.get(context);
  if (existing) return existing;
  const lane: OrgPlanningCommandLane = {
    running: false,
    order: [],
    pending: new Map(),
    coalescibleByTarget: new Map(),
    nextRevision: 0,
    failedByTarget: new Map(),
  };
  lanes.set(context, lane);
  return lane;
}

function maybeReleaseLane(context: string, lane: OrgPlanningCommandLane): void {
  if (!lane.running && lane.order.length === 0 && !lane.barrier && lanes.get(context) === lane) {
    lanes.delete(context);
  }
}

/**
 * Coalesces complete succession slates for one target while preserving one
 * ordered command lane per diagram. A queued lifecycle transition clears the
 * coalescing index, so edits made after its click boundary can never replace a
 * write that the transition is waiting to close.
 */
export function enqueueLatestSuccessionMutation<T>(
  context: string,
  targetRef: string,
  mutation: (signal: AbortSignal) => Promise<T>,
  queryClient?: QueryClient,
): Promise<T> {
  const lane = laneFor(context);
  lane.nextRevision += 1;
  const revision = lane.nextRevision;
  const existingKey = lane.coalescibleByTarget.get(targetRef);
  const existing = existingKey
    ? lane.pending.get(existingKey) as QueuedOrgPlanningMutation<T> | undefined
    : undefined;

  const result = new Promise<T>((resolve, reject) => {
    if (existing) {
      existing.mutation = mutation;
      existing.queryClient = queryClient;
      existing.revision = revision;
      existing.waiters.push({ resolve: (value) => resolve(value as T), reject });
      return;
    }
    queueSequence += 1;
    const queueKey = `mutation:${queueSequence}`;
    lane.order.push(queueKey);
    lane.pending.set(queueKey, {
      queueKey,
      mutation,
      abortController: new AbortController(),
      queryClient,
      revision,
      targetRef,
      waiters: [{ resolve: (value) => resolve(value as T), reject }],
    });
    lane.coalescibleByTarget.set(targetRef, queueKey);
  });

  startDrain(context, lane);
  return result;
}

/** Queues a non-coalescible command after every contribution accepted before this call. */
export function enqueueOrgPlanningTransition<T>(
  context: string,
  mutation: (signal: AbortSignal) => Promise<T>,
  queryClient?: QueryClient,
  options: OrgPlanningTransitionOptions = {},
): Promise<T> {
  const activeLifecycle = lifecycleTransitions.get(context);
  if (options.lifecycleKey && activeLifecycle) {
    if (activeLifecycle.key === options.lifecycleKey) {
      return activeLifecycle.promise as Promise<T>;
    }
    return Promise.reject(new Error('Another lifecycle action is already in progress for this plan.'));
  }
  const lane = laneFor(context);
  const requiredRevision = lane.nextRevision;
  // This is the click-time fence. Later edits get new queue items after the
  // transition instead of replacing a pre-transition item for the same target.
  lane.coalescibleByTarget.clear();
  lane.nextRevision += 1;
  const revision = lane.nextRevision;
  queueSequence += 1;
  const queueKey = `transition:${queueSequence}`;
  const result = new Promise<T>((resolve, reject) => {
    lane.order.push(queueKey);
    lane.pending.set(queueKey, {
      queueKey,
      targetRef: queueKey,
      mutation,
      abortController: new AbortController(),
      queryClient,
      revision,
      requiredRevision,
      allowFailedPrerequisites: options.allowFailedPrerequisites,
      waiters: [{ resolve: (value) => resolve(value as T), reject }],
    });
  });
  if (options.lifecycleKey) {
    lifecycleTransitions.set(context, { key: options.lifecycleKey, promise: result });
    void result.finally(() => {
      if (lifecycleTransitions.get(context)?.promise === result) {
        lifecycleTransitions.delete(context);
      }
    }).catch(() => undefined);
  }
  startDrain(context, lane);
  return result;
}

/** Backward-compatible domain name for succession Apply/Discard call sites. */
export const enqueueSuccessionTransition = enqueueOrgPlanningTransition;

function startDrain(context: string, lane: OrgPlanningCommandLane): void {
  if (lane.running) return;
  lane.running = true;
  queueMicrotask(() => { void drainLane(context, lane); });
}

async function drainLane(context: string, lane: OrgPlanningCommandLane): Promise<void> {
  while (lane.order.length > 0) {
    const queueKey = lane.order.shift()!;
    const pending = lane.pending.get(queueKey);
    if (!pending) continue;
    lane.pending.delete(queueKey);
    lane.active = pending;
    if (lane.coalescibleByTarget.get(pending.targetRef) === queueKey) {
      lane.coalescibleByTarget.delete(pending.targetRef);
    }
    try {
      await awaitOrgPlanningCommandBarrier(context, pending.queryClient);
      if (pending.requiredRevision !== undefined && !pending.allowFailedPrerequisites) {
        const failedPrerequisite = [...lane.failedByTarget.values()]
          .some((failedRevision) => failedRevision <= pending.requiredRevision!);
        if (failedPrerequisite) {
          throw new Error('A draft change did not save, so the queued transition was cancelled.');
        }
      }
      const value = await pending.mutation(pending.abortController.signal);
      const failedRevision = lane.failedByTarget.get(pending.targetRef);
      if (failedRevision !== undefined && failedRevision <= pending.revision) {
        lane.failedByTarget.delete(pending.targetRef);
      }
      pending.waiters.forEach((waiter) => waiter.resolve(value));
    } catch (error) {
      if (error instanceof OrgPlanningCommandBarrierBlockedError) {
        // Do not let commands already queued behind an unresolved external
        // writeback run against a source that the server marked failed or
        // conflicted. They can be recreated after the user resolves recovery.
        for (const queued of lane.pending.values()) {
          queued.waiters.forEach((waiter) => waiter.reject(error));
        }
        lane.pending.clear();
        lane.order.length = 0;
        lane.coalescibleByTarget.clear();
      }
      if (pending.requiredRevision === undefined) {
        lane.failedByTarget.set(pending.targetRef, pending.revision);
      }
      pending.waiters.forEach((waiter) => waiter.reject(error));
    } finally {
      if (lane.active === pending) lane.active = undefined;
    }
  }
  lane.running = false;
  if (lane.order.length > 0) {
    startDrain(context, lane);
    return;
  }
  maybeReleaseLane(context, lane);
}

function runBarrier(
  context: string,
  lane: OrgPlanningCommandLane,
  barrier: CanonicalConvergenceBarrier,
): Promise<void> {
  const promise = barrier.reconcile().then(() => {
    if (lane.barrier === barrier) lane.barrier = undefined;
    maybeReleaseLane(context, lane);
  }, (error) => {
    // Terminal external failures must release the lane so the waiting command
    // receives the error instead of causing an infinite barrier retry loop.
    if (error instanceof OrgPlanningCommandBarrierBlockedError) {
      if (lane.barrier === barrier) lane.barrier = undefined;
      maybeReleaseLane(context, lane);
    }
    throw error;
  });
  void promise.catch(() => undefined);
  return promise;
}

/** Starts canonical convergence without extending the visible mutation state. */
export function registerOrgPlanningCommandBarrier(input: {
  context: string;
  queryClient: QueryClient;
  folderId: string;
  graphVersion: number;
  /** Resolves to the graph version after an external operation reconciles. */
  waitForCompletion?: () => Promise<number>;
  onDelayed?: () => void;
}): void {
  if (!Number.isFinite(input.graphVersion)) {
    throw new Error('A committed organization command must return a finite graph version.');
  }
  const lane = laneFor(input.context);
  const reconcile = async () => {
    try {
      const committedGraphVersion = input.waitForCompletion
        ? await input.waitForCompletion()
        : input.graphVersion;
      await settlePositionCommand(
        invalidateOrgPlanningQueries(input.queryClient, input.folderId),
        'refresh_timeout',
      );
      const model = input.queryClient.getQueryData<OrgModelProjection>(
        liveOrgModelQueryKey(input.folderId),
      );
      if (!model || model.graphVersion < committedGraphVersion) {
        throw new PositionCommandSettlementError(
          'refresh_timeout',
          undefined,
          'canonical_model_stale',
        );
      }
    } catch (error) {
      input.onDelayed?.();
      throw error;
    }
  };
  const barrier: CanonicalConvergenceBarrier = {
    queryClient: input.queryClient,
    reconcile,
    promise: Promise.resolve(),
  };
  lane.barrier = barrier;
  barrier.promise = runBarrier(input.context, lane, barrier);
}

/** Releases only after canonical model/lifecycle parity. */
export async function awaitOrgPlanningCommandBarrier(
  context: string,
  queryClient?: QueryClient,
): Promise<void> {
  const lane = lanes.get(context);
  const barrier = lane?.barrier;
  if (!lane || !barrier) return;
  if (queryClient && barrier.queryClient !== queryClient) {
    lane.barrier = undefined;
    maybeReleaseLane(context, lane);
    return;
  }
  try {
    await barrier.promise;
  } catch (error) {
    if (lane.barrier !== barrier) throw error;
    barrier.promise = runBarrier(context, lane, barrier);
    await barrier.promise;
  }
}

/** Test/logout seam; lane state contains versions and functions, never protected values. */
export function clearOrgPlanningCommandSequencers(context?: string): void {
  const entries = context
    ? [[context, lanes.get(context)] as const]
    : [...lanes.entries()];
  for (const [key, lane] of entries) {
    if (!lane) continue;
    const error = new DOMException('Organization command cancelled because the session changed.', 'AbortError');
    lane.active?.abortController.abort(error);
    lane.active?.waiters.forEach((waiter) => waiter.reject(error));
    for (const pending of lane.pending.values()) {
      pending.abortController.abort(error);
      pending.waiters.forEach((waiter) => waiter.reject(error));
    }
    lane.pending.clear();
    lane.order.length = 0;
    lanes.delete(key);
    lifecycleTransitions.delete(key);
  }
}

/** Clears every command lane owned by one folder without disturbing other open folders. */
export function clearOrgPlanningCommandSequencersForFolder(folderId: string): void {
  const prefix = `${folderId}:`;
  for (const context of [...lanes.keys()]) {
    if (context.startsWith(prefix)) clearOrgPlanningCommandSequencers(context);
  }
}

/** A user explicitly reverted the visible unsaved contribution for this target. */
export function acknowledgeFailedOrgPlanningMutation(context: string, targetRef: string): void {
  const lane = lanes.get(context);
  if (!lane?.failedByTarget.has(targetRef)) return;
  lane.failedByTarget.delete(targetRef);
  maybeReleaseLane(context, lane);
}

export const clearOrgPlanningCommandBarriers = clearOrgPlanningCommandSequencers;
